"use client";
import { isFreePagePaused } from "@/lib/data/paused-free-pages";

import { useState, type ReactNode } from "react";
import { ChevronDown, ArrowRight } from "lucide-react";
import Link from "next/link";

type ContextualLink = { label: string; subPath: string };
type QA = {
  q: string;
  a: ReactNode;
  items?: string[];
  body?: ReactNode;
  cta?: ContextualLink;
  k?: string;
};

export default function QASection({ items, base }: { items: QA[]; base?: string }) {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div className="divide-y divide-[#dde1e7] overflow-hidden rounded-2xl border-[1.5px] border-[#dde1e7] bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
      {items.map((it, i) => {
        const isOpen = open === i;
        return (
          <div key={it.q}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-gray-50"
            >
              <span className="text-base font-bold text-[#0f172a]">{it.q}</span>
              <ChevronDown
                className="h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200"
                style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                aria-hidden
              />
            </button>
            <div
              style={{
                display: "grid",
                gridTemplateRows: isOpen ? "1fr" : "0fr",
                transition: "grid-template-rows 0.22s ease",
              }}
            >
              <div style={{ overflow: "hidden" }}>
                <div className="border-t border-[#dde1e7] bg-gray-50 px-5 py-4">
                  <p className="text-base leading-relaxed text-gray-600">{it.a}</p>
                  {it.items && (
                    <ul className="mt-2 space-y-1.5">
                      {it.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-base leading-relaxed text-gray-600">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1877F2]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  {it.body && <p className="mt-2 text-base leading-relaxed text-gray-600">{it.body}</p>}
                  {it.cta && base && !isFreePagePaused(base.replace(/^\//, ""), it.cta.subPath) && (
                    <Link
                      href={`${base}/${it.cta.subPath}`}
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1877F2] hover:underline"
                    >
                      <ArrowRight className="h-3.5 w-3.5" />
                      {it.cta.label}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
